import type { WidgetDef, WidgetConfig, WidgetId, WidgetSettings } from "../types/widget";
import {
  Calendar,
  Clock,
  Bell,
  Mic,
  AppWindow,
  Settings,
  Shield,
  Library,
  FileText,
  Monitor,
  ExternalLink,
} from "lucide-react";

/**
 * Widget-Registry - Definition aller verfügbaren Widgets
 */
export const WIDGETS: WidgetDef[] = [
  // System-Widgets (keine App-Zuordnung, immer verfügbar)
  {
    id: "calendar",
    Icon: Calendar,
    defaultSize: "small",
    category: "system",
    system: true,
    pinnable: true,
    hasSettings: false,
  },
  {
    id: "clock",
    Icon: Clock,
    defaultSize: "small",
    category: "system",
    system: true,
    pinnable: true,
    hasSettings: false,
  },
  {
    id: "notifications",
    Icon: Bell,
    defaultSize: "medium",
    category: "system",
    system: true,
    pinnable: true,
    hasSettings: true, // z.B. Anzahl anzuzeigender Notifications
  },
  {
    id: "voice-agent",
    Icon: Mic,
    defaultSize: "medium",
    category: "system",
    system: true,
    pinnable: true,
    hasSettings: false,
    allowedSizes: ["medium", "tall", "large"], // Small entfernt - zu wenig Platz für Steuerelemente
  },
  {
    id: "url-linked",
    Icon: ExternalLink,
    defaultSize: "medium",
    category: "system",
    system: true,
    pinnable: true,
    hasSettings: true,
    allowedSizes: ["small", "medium", "tall", "large"],
  },
  {
    id: "app-store",
    Icon: AppWindow,
    defaultSize: "medium",
    category: "apps",
    appId: "app-store",
    system: true, // App Store ist System-App, also immer verfügbar
    pinnable: true,
    hasSettings: true, // z.B. Anzahl anzuzeigender Apps
  },
  {
    id: "settings",
    Icon: Settings,
    defaultSize: "medium",
    category: "apps",
    appId: "settings",
    system: true, // Settings ist System-App, also immer verfügbar
    pinnable: true,
    hasSettings: false,
    allowedSizes: ["medium", "large"], // Settings macht nur in größeren Größen Sinn
  },
  {
    id: "prompt-library",
    Icon: Library,
    defaultSize: "medium",
    category: "productivity",
    appId: "prompt-library",
    pinnable: true,
    hasSettings: true, // z.B. nur Favoriten anzeigen
  },
  {
    id: "pdf-viewer",
    Icon: FileText,
    defaultSize: "medium",
    category: "productivity",
    appId: "drive2", // files open in the preview (#1648)
    pinnable: true,
    hasSettings: true, // z.B. Anzahl zuletzt geöffneter PDFs
    allowedSizes: ["medium", "tall", "large"], // Small entfernt - zu wenig Platz für PDFs
  },
  {
    id: "multimedia",
    Icon: Monitor,
    defaultSize: "medium",
    category: "productivity",
    appId: "drive2", // Drive's "Audio und Video" filter (#1648)
    pinnable: true,
    hasSettings: false,
  },
  {
    id: "admin",
    Icon: Shield,
    defaultSize: "medium",
    category: "admin",
    appId: "cortex",
    adminOnly: true,
    pinnable: true,
    hasSettings: false,
    allowedSizes: ["medium", "tall", "large"], // Small entfernt - zu wenig Platz für Admin-Panel
  },
];

/**
 * Standard Widget-Konfigurationen für neue User
 * Nur die wichtigsten Widgets sind standardmäßig aktiviert
 */
export const DEFAULT_WIDGET_CONFIGS: WidgetConfig[] = [
  // System-Widgets (standardmäßig aktiviert)
  {
    id: "calendar",
    enabled: true,
    size: "small",
    position: 0,
    pinned: false,
    settings: {},
  },
  {
    id: "clock",
    enabled: true,
    size: "small",
    position: 1,
    pinned: false,
    settings: {},
  },
  {
    id: "notifications",
    enabled: true,
    size: "medium",
    position: 2,
    pinned: false,
    settings: {
      maxItems: 3,
    },
  },
  {
    id: "voice-agent",
    enabled: true,
    size: "medium",
    position: 3,
    pinned: false,
    settings: {},
  },

  // App-Widgets (standardmäßig deaktiviert - User kann sie aktivieren)
  {
    id: "app-store",
    enabled: false,
    size: "medium",
    position: 4,
    pinned: false,
    settings: {
      maxItems: 3,
    },
  },
  {
    id: "settings",
    enabled: false,
    size: "medium",
    position: 5,
    pinned: false,
    settings: {},
  },
  {
    id: "prompt-library",
    enabled: false,
    size: "medium",
    position: 10,
    pinned: false,
    settings: {
      maxItems: 3,
    },
  },
  {
    id: "pdf-viewer",
    enabled: false,
    size: "medium",
    position: 12,
    pinned: false,
    settings: {
      maxItems: 3,
    },
  },
  {
    id: "multimedia",
    enabled: false,
    size: "medium",
    position: 13,
    pinned: false,
    settings: {},
  },
  {
    id: "admin",
    enabled: false,
    size: "medium",
    position: 14,
    pinned: false,
    settings: {},
  },
  {
    id: "url-linked",
    enabled: false,
    size: "medium",
    position: 15,
    pinned: false,
    settings: {
      links: [],
      displayMode: "list",
      maxLinks: 5,
    },
  },
];

/** The widgets of the widget bar, in their starting order (#1822). */
export const WIDGET_BAR_CATALOG: WidgetId[] = ["clock", "calendar", "voice-agent", "prompt-library"];

/** One way a widget can sit in the bar. */
export interface WidgetBarVariant {
  id: string;
  size: "small" | "medium";
  settings?: WidgetSettings;
}

const DEFAULT_BAR_VARIANTS: WidgetBarVariant[] = [
  { id: "small", size: "small" },
  { id: "medium", size: "medium" },
];

export const getWidgetBarVariants = (widgetId: WidgetId): WidgetBarVariant[] =>
  widgetId === "clock"
    ? [
        { id: "medium", size: "medium", settings: { clockStyle: "digital" } },
        { id: "smallDigital", size: "small", settings: { clockStyle: "digital" } },
        { id: "smallAnalog", size: "small", settings: { clockStyle: "analog" } },
      ]
    : DEFAULT_BAR_VARIANTS;

export const getActiveWidgetBarVariant = (config: WidgetConfig): WidgetBarVariant => {
  const size = config.size === "small" ? "small" : "medium";
  const variants = getWidgetBarVariants(config.id).filter((v) => v.size === size);
  return variants.find((v) => v.settings?.clockStyle === config.settings?.clockStyle) ?? variants[0];
};

/**
 * Brings saved configurations into the bar once: catalog widgets leave the
 * desktop and take a size the bar has, and prompts join the starting set. A
 * widget someone switched off stays off. Other widgets keep their saved state,
 * so switching the flag off brings them back.
 */
export const seedWidgetBar = (configs: WidgetConfig[]): WidgetConfig[] => {
  const inCatalog = (config: WidgetConfig) => WIDGET_BAR_CATALOG.includes(config.id);
  const seeded = WIDGET_BAR_CATALOG.flatMap((id) => configs.filter((c) => c.id === id)).map((config) => ({
    ...config,
    size: "medium" as const,
    enabled: config.enabled || config.id === "prompt-library",
    pinnedToDesktop: false,
    desktopPosition: undefined,
    settings: {
      ...config.settings,
      ...(config.id === "clock" ? { clockStyle: "digital" } : {}),
      widgetBarSeeded: true,
    },
  }));
  return [...seeded, ...configs.filter((config) => !inCatalog(config))];
};

export const isWidgetBarSeeded = (configs: WidgetConfig[]): boolean =>
  configs.some((config) => config.settings?.widgetBarSeeded === true);

// Hilfsfunktionen

/**
 * Holt Widget-Definition nach ID
 */
export const getWidgetById = (widgetId: string): WidgetDef | undefined => {
  return WIDGETS.find((w) => w.id === widgetId);
};

/**
 * Holt Widgets für einen bestimmten User basierend auf App-Berechtigungen
 *
 * @param userAssignedApps - Array von App-IDs die dem User zugewiesen sind
 * @param userRole - User-Rolle ('admin' | 'user')
 * @returns Array von verfügbaren Widget-Definitionen
 */
export const getWidgetsForUser = (
  userAssignedApps: string[] = [],
  userRole: "admin" | "user" = "user"
): WidgetDef[] => {
  return WIDGETS.filter((widget) => {
    // System-Widgets sind immer verfügbar
    if (widget.system) return true;

    // Admin-Widgets nur für Admins
    if (widget.adminOnly) return userRole === "admin";

    // App-basierte Widgets: User muss Zugriff auf die App haben
    if (widget.appId) {
      // Admins haben Zugriff auf alle Widgets
      if (userRole === "admin") return true;

      // User muss die App zugewiesen haben
      return userAssignedApps.includes(widget.appId);
    }

    // Fallback: Widget verfügbar
    return true;
  });
};
